interface ThemeConfig {
  id: number;
  name: string;
  app_type?: string;
  is_active: boolean;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  background_color: string;
  accent_color: string;
  success_color?: string;
  warning_color?: string;
  error_color?: string;
  info_color?: string;
  border_color?: string;
  card_color?: string;
  header_color?: string;
  footer_color?: string;
  logo_url?: string;
  font_family: string;
  version: number;
}

import { API_ENDPOINTS } from '../config/api';

class ThemeService {
  private cache: {
    theme?: ThemeConfig;
    lastFetch?: number;
  } = {};
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  private getAuthToken(): string | null {
    try {
      const sessionData = localStorage.getItem('adminSession');
      console.log('🔍 [THEME SERVICE] Session data:', sessionData ? 'Found' : 'Not found');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        console.log('🔍 [THEME SERVICE] Full session:', session);
        console.log('🔍 [THEME SERVICE] Token length:', session.access ? session.access.length : 0);
        console.log('🔍 [THEME SERVICE] Token preview:', session.access ? `${session.access.substring(0, 50)}...` : 'No token');
        return session.access;
      }
      return null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  async getThemeConfig(): Promise<ThemeConfig | null> {
    try {
      // Check cache first
      if (this.cache.theme && this.cache.lastFetch && 
          Date.now() - this.cache.lastFetch < this.cacheTimeout) {
        return this.cache.theme;
      }

      console.log('🎨 Using default theme (Django API disabled)');
      // Skip theme fetching - Django API returns HTML
      const defaultTheme = this.getDefaultTheme();
      this.cache.theme = defaultTheme;
      this.cache.lastFetch = Date.now();
      return defaultTheme;
    } catch (error) {
      console.error('❌ Failed to load theme config:', error);
      // Return default theme as fallback
      return this.getDefaultTheme();
    }
  }

  private getDefaultTheme(): ThemeConfig {
    return {
      id: 0,
      name: 'Default Admin Theme',
      app_type: 'admin',
      is_active: true,
      primary_color: '#00C896',
      secondary_color: '#0A2540',
      text_color: '#e0e0e0',
      background_color: '#1a1d2e',
      accent_color: '#00C896',
      success_color: '#00C896',
      warning_color: '#FF9800',
      error_color: '#F44336',
      info_color: '#2196F3',
      border_color: '#3a3d52',
      card_color: '#252838',
      header_color: '#1e2139',
      footer_color: '#0A2540',
      logo_url: '',
      font_family: 'Poppins',
      version: 1,
    };
  }

  // Apply theme to the DOM
  applyTheme(theme: ThemeConfig) {
    const root = document.documentElement;
    
    // Set CSS custom properties
    root.style.setProperty('--primary-color', theme.primary_color);
    root.style.setProperty('--secondary-color', theme.secondary_color);
    root.style.setProperty('--text-color', theme.text_color);
    root.style.setProperty('--background-color', theme.background_color);
    root.style.setProperty('--accent-color', theme.accent_color);
    root.style.setProperty('--font-family', theme.font_family);
    
    // Update document body styles
    document.body.style.backgroundColor = theme.background_color;
    document.body.style.color = theme.text_color;
    document.body.style.fontFamily = theme.font_family;
    
    // Force apply to html element as well
    document.documentElement.style.backgroundColor = theme.background_color;
    document.documentElement.style.color = theme.text_color;
    
    // Apply to root div if it exists
    const rootDiv = document.getElementById('root');
    if (rootDiv) {
      rootDiv.style.backgroundColor = theme.background_color;
      rootDiv.style.color = theme.text_color;
      rootDiv.style.fontFamily = theme.font_family;
    }
    
    // Update favicon if logo_url is provided
    if (theme.logo_url) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = theme.logo_url;
      }
    }
    
    console.log('🎨 Theme applied:', {
      background: theme.background_color,
      text: theme.text_color,
      primary: theme.primary_color
    });
  }

  // Create Material-UI theme object
  createMuiTheme(theme: ThemeConfig) {
    return {
      palette: {
        mode: 'dark' as const,
        primary: {
          main: theme.primary_color,
          light: theme.primary_color,
          dark: theme.primary_color,
          contrastText: theme.text_color,
        },
        secondary: {
          main: theme.secondary_color,
          light: theme.secondary_color,
          dark: theme.secondary_color,
          contrastText: theme.text_color,
        },
        background: {
          default: theme.background_color,
          paper: theme.card_color || theme.background_color,
        },
        text: {
          primary: theme.text_color,
          secondary: theme.text_color,
        },
        success: {
          main: theme.success_color || theme.accent_color,
        },
        warning: {
          main: theme.warning_color || '#ffc107',
        },
        error: {
          main: theme.error_color || '#dc3545',
        },
        info: {
          main: theme.info_color || theme.accent_color,
        },
      },
      typography: {
        fontFamily: theme.font_family,
        h1: {
          fontSize: '2.5rem',
          fontWeight: 700,
        },
        h2: {
          fontSize: '2rem',
          fontWeight: 700,
        },
        h3: {
          fontSize: '1.75rem',
          fontWeight: 600,
        },
        h4: {
          fontSize: '1.5rem',
          fontWeight: 600,
        },
        h5: {
          fontSize: '1.25rem',
          fontWeight: 600,
        },
        h6: {
          fontSize: '1.1rem',
          fontWeight: 600,
        },
        body1: {
          fontSize: '1rem',
          fontWeight: 400,
        },
        body2: {
          fontSize: '0.875rem',
          fontWeight: 400,
        },
        button: {
          textTransform: 'none' as const,
          fontWeight: 600,
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: theme.background_color,
              color: theme.text_color,
              fontFamily: theme.font_family,
            },
            html: {
              backgroundColor: theme.background_color,
            },
            '#root': {
              backgroundColor: theme.background_color,
              minHeight: '100vh',
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: theme.header_color || theme.background_color,
              color: theme.text_color,
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: theme.secondary_color,
              color: theme.text_color,
              borderRight: `1px solid ${theme.border_color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              '&.Mui-selected': {
                backgroundColor: theme.primary_color,
                color: theme.text_color,
                '&:hover': {
                  backgroundColor: theme.primary_color,
                  opacity: 0.8,
                },
              },
              '&:hover': {
                backgroundColor: `${theme.primary_color}20`,
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              backgroundColor: theme.primary_color,
              color: theme.text_color,
              '&:hover': {
                backgroundColor: theme.primary_color,
                opacity: 0.8,
              },
            },
            contained: {
              backgroundColor: theme.primary_color,
              color: theme.text_color,
            },
            outlined: {
              borderColor: theme.primary_color,
              color: theme.primary_color,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: theme.card_color || theme.background_color,
              color: theme.text_color,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderRadius: '8px',
              border: `1px solid ${theme.border_color}`,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: theme.card_color || theme.background_color,
              color: theme.text_color,
            },
          },
        },
      },
    };
  }

  // Clear cache (useful for forcing refresh)
  clearCache() {
    this.cache = {};
  }
}

export const themeService = new ThemeService();
export type { ThemeConfig };